import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const PATH_LABELS: Record<string, string> = {
  reports: 'รายงาน',
  'tax-invoices': 'ใบกำกับภาษี',
  daily: 'ยอดขายรายวัน',
  monthly: 'ยอดขายรายเดือน',
  'by-branch': 'ยอดขายแยกสาขา',
  'product-sales': 'ยอดขายและกำไรรายสินค้า',
  rounds: 'รอบขาย & เงินสด',
  vat: 'สรุป VAT',
  inventory: 'รายงานสต๊อก',
  products: 'รายการสินค้า',
  categories: 'หมวดหมู่',
  pricing: 'ราคาแยกสาขา',
  stock: 'สต๊อกแยกสาขา',
  movements: 'ประวัติการเคลื่อนไหว',
  transfers: 'โอนย้ายระหว่างสาขา',
  'goods-receive': 'รับเข้าสินค้า',
  users: 'ผู้ใช้และสิทธิ์',
  branches: 'ร้านค้าและสาขา',
  'payment-methods': 'วิธีชำระเงิน',
  promotions: 'โปรโมชั่น',
  audit: 'ประวัติการใช้งาน',
  settings: 'ตั้งค่าระบบ',
  operations: 'ระบบ/ปฏิบัติการ',
  'sync-health': 'สถานะ Sync',
  reconciliation: 'กระทบยอดชำระ',
  evidence: 'ตรวจหลักฐานโอน',
  devices: 'ระบบอุปกรณ์',
  health: 'สถานะเครื่อง',
  backups: 'สำรองข้อมูล',
  new: 'สร้างใหม่',
  edit: 'แก้ไข',
};

function getPathLabel(path: string, segment: string): string {
  if (path === '/stock/adjust') return 'ปรับสต๊อก';
  if (path === '/products/new') return 'เพิ่มสินค้า';
  if (path === '/products/import') return 'นำเข้า Excel';
  if (/^\/products\/[^/]+$/.test(path)) return 'แก้ไขสินค้า';
  if (path === '/transfers/new') return 'บันทึกโอนย้าย';
  if (/^\/transfers\/[^/]+$/.test(path)) return 'รายละเอียดการโอน';
  return PATH_LABELS[segment] || segment;
}

const LINKABLE_PATHS = new Set([
  '/',
  '/products',
  '/categories',
  '/pricing',
  '/stock',
  '/movements',
  '/transfers',
  '/goods-receive',
  '/users',
  '/branches',
  '/payment-methods',
  '/promotions',
  '/audit',
  '/settings',
  '/tax-invoices',
]);

function Breadcrumbs() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (pathname === '/') return null;

  const segments = pathname.split('/').filter(Boolean);
  const trail: { label: string; to: string; isLink: boolean }[] = [
    { label: 'ภาพรวม', to: '/', isLink: true },
  ];

  let cumulativePath = '';
  segments.forEach((segment) => {
    cumulativePath += `/${segment}`;
    const label = getPathLabel(cumulativePath, segment);
    const isLink = LINKABLE_PATHS.has(cumulativePath);
    trail.push({ label, to: cumulativePath, isLink });
  });

  const parentPath = '/' + segments.slice(0, -1).join('/');
  const isSubmenu =
    segments.length > 1 && parentPath !== '/' && LINKABLE_PATHS.has(parentPath);

  return (
    <div className="flex items-center gap-2 mb-5 text-sm-label select-none">
      {isSubmenu && (
        <button
          onClick={() => navigate(parentPath)}
          className="flex items-center gap-1.5 text-ink-3 hover:text-primary transition-colors mr-2 pr-3 border-r border-line-2 font-medium"
          aria-label="ย้อนกลับ"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-3.5 h-3.5"
          >
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          <span>ย้อนกลับ</span>
        </button>
      )}
      <nav className="flex items-center gap-1.5 text-ink-3 font-medium">
        {trail.map((item, idx) => {
          const isLast = idx === trail.length - 1;
          return (
            <div key={item.to} className="flex items-center gap-1.5">
              {idx > 0 && (
                <span className="text-ink-4 text-[10px] select-none">/</span>
              )}
              {item.isLink && !isLast ? (
                <Link
                  to={item.to}
                  className="hover:text-primary hover:underline transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className={isLast ? 'text-ink font-semibold' : ''}>
                  {item.label}
                </span>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

export function Shell() {
  return (
    <div className="flex">
      <Sidebar />
      <div className="flex-1 min-w-0 flex flex-col h-screen">
        <TopBar />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-bg">
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
